import React, { useState, useEffect } from 'react';
import { useAuthState } from 'react-firebase-hooks/auth'; 
import { auth, db } from '../firebase';
import { getDocs, query, where, collection, onSnapshot, addDoc } from 'firebase/firestore';

import { CircularProgress, Button, Box, Typography, Stepper, Step, StepLabel } from '@mui/material';

import logo from '../images/logo.svg';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';


function Plan() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const steps = [
    <>
      <Typography align="center">アカウント<br />作成</Typography>
    </>,
    <>
      <Typography align="center">店舗情報<br />設定</Typography>
    </>,
    <>
      <Typography align="center">決済情報<br />登録</Typography>
    </>,
  ];

  const fetchData = async () => {
    try {
      // プラン一覧を取得
      const productsCollection = collection(db, 'products');
      const q = query(productsCollection, where('active', '==', true));
      const querySnapshot = await getDocs(q);

      // 取得したプラン一覧から1プランずつ金額などの情報を取得
      const productData = [];

      // プラン一覧をから金額情報を取得
      const promises = querySnapshot.docs.map(async (doc) => {
        const priceCollection = collection(doc.ref, 'prices');
        const priceQuery = query(priceCollection, where('active', '==', true));
        const priceSnapshot = await getDocs(priceQuery);
        
        const product = doc.data();
        const prices = {};

        priceSnapshot.forEach((priceDoc) => {
          const priceId = priceDoc.id;
          const priceData = priceDoc.data();
          prices[priceId] = priceData;
        });
        
        productData.push({
          id: doc.id,
          product,
          prices,
        });
      });
      
      // forEachやmapでは非同期の処理を待たないため、for...ofかPromis.allで処理を待つ
      await Promise.all(promises);

      // useStateにプラン情報をセット
      setProducts(productData);

    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false); // ローディング終了
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </div>
    );
  }
  
  return (
      <Box sx={{ minHeight: '100vh', backgroundColor: '#ffffff', px: 0, py: 0 }}>
      <Box sx={{ backgroundColor:'#f9db00', textAlign: 'center', mb: 2, py: 2 }}>
        <img src={logo} alt="Conekuta" style={{ height: '1.25rem' }} />
      </Box>
      <Box sx={{ width: "100%" }}>
        <Stepper activeStep={2} alternativeLabel>
          {steps.map((label, index) => (
            <Step key={index}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>
      <Box sx={{ maxWidth: 400, mx: 'auto', textAlign: 'center', px: 3 }}>
        <Typography variant="h6" fontWeight="bold" sx={{ mt: 4, mb: 2 }}>
          プランを選択
        </Typography>
      </Box>
      <div
        className="products"
        style={{
          width: '100%',
          marginTop: '0',
          display: 'flex',
          justifyContent: 'center'
        }}
      >
        {products.map((product) => (
          <ProductItem key={product.id} product={product} />
        ))}
      </div>
    </Box>
  );

}

export default Plan;


function ProductItem({ product }) {
  const { product: productData, prices } = product;
  const [user] = useAuthState(auth);

  // TODO:複数プランから選択可能にする場合は以下の対応をする
  // 選択したプランの情報をstateで管理する
  // 選択したプランのpricesから対象のドキュメントを取得する
  // 例）product.prices.price_1LgjKWFSs4DMiXAdzEJc4pwI
  // その値をselectedPriceに入れる

  const subscribe = async (product) => {
    const selectedPrice = {
      // プランが1つのため、固定値で入れている
      price: process.env.REACT_APP_STRIPE_PRICE_ID,
      quantity: 1,
    };
    
    const checkoutSession = {
      automatic_tax: true,
      cancel_url: `${window.location.origin}/plan`,
      collect_shipping_address: true,
      line_items: [selectedPrice],
      metadata: {
        key: 'value',
      },
      mode: 'subscription',
      trial_period_days: 30,
      success_url: window.location.origin,
      tax_id_collection: true,
      subscription_data: {
        items: [
          {
            price: process.env.REACT_APP_STRIPE_PRICE_ID
          }
        ],
      },
    };

    const docRef = await addDoc(collection(db, 'customers', user.uid, 'checkout_sessions'), checkoutSession);
    
    onSnapshot(docRef, (snap) => {
      const { error, url } = snap.data();
      if (error) {
        alert(`An error occurred: ${error.message}`);
        document.querySelectorAll('button').forEach((b) => (b.disabled = false));
      }
      if (url) {
        window.location.assign(url);
      }
    });

  };

  return (
    <div
      className="product"
      style={{
        display: 'flex'
        , flexDirection: 'column'
        , alignItems: 'center'
        , justifyContent: 'center'
        , textAlign: 'center'
        // , padding: '16px'
        , width: 'auto'
        , margin: '0'
      }}
    >

      <h2 style={{margin: '0'}}>{productData.name.toUpperCase()}</h2>

      <p className="description">{productData.description?.toUpperCase() || ''}</p>

      <select id="price">
        {Object.entries(prices).map(([priceId, priceData]) => (
          <option key={priceId} value={priceId}>
            {new Intl.NumberFormat('ja-JP', {
              style: 'currency',
              currency: 'JPY',
            }).format(priceData.unit_amount.toFixed(2))}
            (月額)
          </option>
        ))}
      </select>

      {productData.images.length > 0 && (
        <img src={productData.images[0]} alt={productData.name} />
      )}

      <Button
        variant="contained"
        color="primary"
        onClick={() => subscribe(product)}
        sx={{ mt: 2, width: '100%', fontWeight: 'bold' }}
        endIcon={<ArrowForwardIosIcon fontSize="small" />}
      >
        次へ
      </Button>
      
    </div>
  );

}


